import { useState } from "react";
import { Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch, PageHeader, Panel, Loading, StatusBadge, Table, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function InvestDialog({ plan, onClose, onDone }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const daily = (Number(amount || 0) * (plan?.daily_percent || 0)) / 100;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/investments", { plan_id: plan.id, amount: Number(amount) });
      toast.success(`${plan.name} activated`);
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121722] border-[#242F42] text-white" data-testid="invest-dialog">
        <DialogHeader><DialogTitle className="font-display">Activate {plan?.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-slate-400">Available balance: <span className="num text-[#10B981]">{usd(user?.balance)}</span></p>
          <div>
            <label className="nx-label">Amount ({usd(plan?.min_amount)} – {usd(plan?.max_amount)})</label>
            <input type="number" step="0.01" required className="nx-input num" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="invest-amount-input" />
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-3"><p className="text-slate-500">Est. daily</p><p className="num mt-1 text-[#10B981]">{usd(daily)}</p></div>
            <div className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-3"><p className="text-slate-500">Est. total ({plan?.duration_days}d)</p><p className="num mt-1 text-white">{usd(daily * (plan?.duration_days || 0))}</p></div>
          </div>
          <p className="text-[11px] text-slate-500">Estimates use the current plan rate. Principal is returned at the end of the term.</p>
          <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="confirm-invest-btn">{busy && <Loader2 size={16} className="animate-spin" />}Activate plan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Plans() {
  const { reload: reloadUser } = useAuth();
  const { data: plans, loading } = useFetch("/plans");
  const { data: invs, reload } = useFetch("/investments");
  const [sel, setSel] = useState(null);
  if (loading) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="AI Trading" title="AI trading plans" subtitle="Returns are credited to your balance once per day for the plan duration." />
      <div className="grid gap-6 md:grid-cols-3" data-testid="plans-grid">
        {plans?.map((p, i) => (
          <div key={p.id} className={`nx-card flex flex-col p-6 transition-colors hover:border-[#10B981]/40 ${i === 1 ? "border-[#10B981]/40" : ""}`} data-testid={`plan-card-${p.id}`}>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#10B981]/10 text-[#10B981]"><Bot size={18} /></span>
              <div><h3 className="font-semibold text-white">{p.name}</h3><p className="text-xs text-slate-500">{p.description}</p></div>
            </div>
            <p className="num mt-6 text-4xl font-semibold text-[#10B981]">{p.daily_percent}%<span className="ml-1 text-sm font-normal text-slate-400">/ day</span></p>
            <div className="mt-5 space-y-2 border-t border-[#1E2738] pt-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Range</span><span className="num text-white">{usd(p.min_amount)} – {usd(p.max_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Duration</span><span className="num text-white">{p.duration_days} days</span></div>
            </div>
            <button className="nx-btn-primary mt-6" onClick={() => setSel(p)} data-testid={`invest-btn-${p.id}`}>Activate plan</button>
          </div>
        ))}
      </div>
      <Panel title="My investments">
        <Table head={["Started", "Plan", "Amount", "Rate", "Progress", "Earned", "Status"]} testid="investments-table">
          {!invs?.length && <Empty cols={7} text="No active plans yet" />}
          {invs?.map((v) => (
            <tr key={v.id}>
              <td className="nx-td">{fmtDate(v.created_at)}</td>
              <td className="nx-td text-white">{v.plan_name}</td>
              <td className="nx-td num">{usd(v.amount)}</td>
              <td className="nx-td num">{v.daily_percent}%</td>
              <td className="nx-td">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 rounded-full bg-[#1E2738]"><div className="h-1.5 rounded-full bg-[#10B981]" style={{ width: `${(v.days_paid / v.duration_days) * 100}%` }} /></div>
                  <span className="num text-xs">{v.days_paid}/{v.duration_days}</span>
                </div>
              </td>
              <td className="nx-td num text-[#10B981]">{usd(v.total_earned)}</td>
              <td className="nx-td"><StatusBadge status={v.status} /></td>
            </tr>
          ))}
        </Table>
      </Panel>
      <InvestDialog plan={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); reload(); reloadUser(); }} />
    </div>
  );
}
